import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Modal, Alert, Button, Card, Col, Form, Row, Table, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { CampaignSummary, CampaignsResponseDto, CampaignsResponseDtoCampaign, App, UserInfo, ClinkFromServer, PublisherOrSiteOption, User, CountryOption, PayoutMode, Message, DataType, Validation, PublisherRecord, PublisherSiteRecord, MessageType, ResponseStatus } from "../../utils/types";
import { canEditClinkField } from "../../utils/UserHelper";
import { Campaigns } from "../../services/Campaigns";
import { TrafficSources } from "../../services/TrafficSources";
import { Users } from "../../services/Users";
import { isGreaterOSVersion } from "../../utils/misc";
import { DatagridHelper } from "../../datagrid/DatagridHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faMinus } from "@fortawesome/free-solid-svg-icons";
import { Clinks } from "../../services/Clinks";
import ModalHeader from "../modal/ModalHeader";
import ModalNotificaton from "../notifications/ModalNotificaton";
import CampaignsTable from "./CampaignsTable";
import ExistingClinksTable from "./ExistingClinksTable";
import TrafficSourcesAutocomplete, { TrafficSourceLabelKeyFunction } from "../TrafficSourcesAutocomplete";
import AutocompleteSingle from "../library/AutocompleteSingle";
import CountriesAutocomplete from "../CountriesAutocomplete";
import COUNTRIES from "../countries";
import InfoIcon from "./info/InfoIcon";
import ValidationsWrapper from "../validation/ValidationsWrapper";
import OverrideCounterBidingTooltip from "./info/OverrideCounterBidingTooltip";
import AdminCounterBidingTooltip from "./info/AdminCounterBidingTooltip";
// support_tags/notes
// import { Tags } from "../../services/Tags";
import './ClinkForm.css';
interface ModalCreateClinkProps {
  app: App;
  closeCreateClink: () => void;
  campaign?: CampaignsResponseDtoCampaign;
  currentUser: UserInfo;
}

interface ApplyToAllPartnerRowData {
  owner: User,
  countries: CountryOption[],
  payoutMode: PayoutMode,
  tags: string[],
  dailyCap: number,
  p2p: number
}

interface PartnerRowData extends ApplyToAllPartnerRowData {
  trafficSource: PublisherOrSiteOption,
  payoutMode: PayoutMode,
  publisherRevShare: number,
  margin: string
}

const UDSformat = { style: 'currency',currency: 'USD'};
const roisenseId = "c4n3aaqqwmbn_rois";
const trafficMediaId = "peji2rbvek2u_medcon";

const cardHeaderStyle = {
  fontWeight: "bold",
  fontSize: "1.2rem"
}

const determineRevShareInfo = (revShare:number, campaignSummary:CampaignSummary) => {
  const minCPI = campaignSummary.minPayout;
  const maxCPI = campaignSummary.maxPayout;

  if (minCPI === maxCPI) {
    return DatagridHelper.formatValue(DataType.MONEY, revShare * minCPI) + " @ " + DatagridHelper.formatValue(DataType.PERCENTAGE, revShare );
  }
  return DatagridHelper.formatValue(DataType.MONEY, revShare * minCPI) + " - " + DatagridHelper.formatValue(DataType.PERCENTAGE, revShare * maxCPI) + " @ " + DatagridHelper.formatValue(DataType.PERCENTAGE, revShare );
}

const determineMarginInfo = (split:number, payout:number, campaignSummary:CampaignSummary) => {
  const minCPI = campaignSummary.minPayout;
  const maxCPI = campaignSummary.maxPayout;

  // Determine the margin info text which can show either a single value or a range.
  var marginPercentage;
  if (split != null) {
    marginPercentage = DatagridHelper.formatValue(DataType.PERCENTAGE, 1.0 - split);
  } else {
    marginPercentage = DatagridHelper.formatValue(DataType.PERCENTAGE, ( minCPI - payout ) / minCPI );
    if ( minCPI !== maxCPI ) {
      marginPercentage = marginPercentage + " - " + DatagridHelper.formatValue(DataType.PERCENTAGE, ( maxCPI - payout ) / maxCPI );
    }
  }
  
  var margin =  DatagridHelper.formatValue(DataType.MONEY, minCPI - payout);
  if ( minCPI !== maxCPI ) {
    margin = margin + " - " + DatagridHelper.formatValue(DataType.MONEY, maxCPI - payout );
  }
  
  if (split != null) {
    return margin + " @ " + marginPercentage;
  }

  return margin + " (" + marginPercentage + ")";
};


function ModalCreateClink(props:ModalCreateClinkProps) {
  const { app, closeCreateClink, campaign, currentUser } = props;
  const [creationSuccess, setCreationSuccess] = useState<boolean>(false);
  const [showCreateClinkFormData, setShowCreateClinkFormData] = useState<boolean>(true);
  const [campaignsResponseDto, setCampaignsResponseDto] = useState<CampaignsResponseDto>(null);
  const [clinkableTrafficSources, setClinkableTrafficSources] = useState<PublisherOrSiteOption[]>(null);
  const [users, setUsers] = useState([]);
  // support_tags/notes
  // const [mostFrequentTags, setMostFrequentTags] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showModalNotification, setShowModalNotification] = useState(false);
  const [addClinksIsLoading, setAddClinksIsLoading] = useState<boolean>(false);
  const [partnerRowsData, setPartnerRowsData] = useState<PartnerRowData[]>([]);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  const [applyToAllPartnerRowData, setApplyToAllPartnerRowData] = useState<ApplyToAllPartnerRowData>({
    owner: users.find((user) => user.loginEmail === currentUser.email),
    countries: countryOptions,
    payoutMode: PayoutMode.NO_PAYOUT,
    tags: [],
    dailyCap: 0,
    p2p: 0
  });
  const [ignorePlatforms, setIgnorePlatforms] = useState<boolean>(app.appID.startsWith("tile.") ? true : false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [allowCounterBidOverride, setAllowCounterBidOverride] = useState("default");
  const [allowCounterBid, setAllowCounterBid] = useState("default");
  const [search, setSearch] = useState("");
  const [validations, setValidations] = useState<Validation[]>([]);
  const [validationMessage, setValidationMessage] = useState("");
  const [defaultSelectedCampaigns, setDefaultSelectedCampaigns] = useState<boolean>(false);

  const trafficSourcesRef = useRef(null);
  const applyToAllCountriesRef = useRef(null);
  // support_tags/notes
  // const applyToAllTagsRef = useRef(null);
  const applyToAllDailyCapRef = useRef(null);
  const applyToAllP2pRef = useRef(null);
  const countriesRefs = useRef([]);
  // support_tags/notes
  // const tagsRefs = useRef([]);
  const payoutRefs = useRef([]);
  const dailyCapRefs = useRef([]);
  const createButtonRef = useRef(null);

  if (campaign && campaignsResponseDto && campaignsResponseDto.campaigns && !defaultSelectedCampaigns && !campaignsResponseDto.campaigns.find((c => c.added))) {
    const selectedCampaogn:CampaignsResponseDtoCampaign = campaignsResponseDto.campaigns.find((c) => c.campaignID === campaign.campaignID && c.supplySource === campaign.supplySource);
    if (selectedCampaogn) {
      selectedCampaogn.added = true;
      setCampaignsResponseDto({
        ...campaignsResponseDto, 
        campaigns: [selectedCampaogn]
      });
      setDefaultSelectedCampaigns(true);
    }
  }

  const existingCLinks:ClinkFromServer[] = campaignsResponseDto?.clinks || [];

  const calculateCampaignSummary = (campaignsResponseDto:CampaignsResponseDto) => {
    const campaignSummary:CampaignSummary = {
      numberOfCampaigns: 0,
      totalCap: 0,
      countries: [],
      platforms: [],
      osVersion: "None",
      incent: false,
      minPayout: 0,
      maxPayout: 0,
      payoutRange: "None"
    };
    if (campaignsResponseDto) {
      campaignsResponseDto.campaigns.forEach((campaign) => {
        if (!campaign.added) return;
        campaignSummary.numberOfCampaigns++;
  
        const countries = campaign.countries.split(",");
        countries.forEach((country:string) => {
          if (!campaignSummary.countries.includes(country)) {
            campaignSummary.countries.push(country);
          }
        });
        const platforms = campaign.platforms.split(",");
        platforms.forEach((platform:string) => {
          if (!campaignSummary.platforms.includes(platform)) {
            campaignSummary.platforms.push(platform);
          }
        });
  
        if (campaign.osVersion !== "Any") {
          if (campaignSummary.osVersion === "None") {
            campaignSummary.osVersion = campaign.osVersion;
          } else if (isGreaterOSVersion( campaignSummary.osVersion, campaign.osVersion)) {
            campaignSummary.osVersion = campaign.osVersion;
          }
        }
  
        if (campaign.dailyInstallCap && campaign.dailyInstallCap > 0) {
          campaignSummary.totalCap += campaign.dailyInstallCap;
        }
  
        if (campaign.incent) campaignSummary.incent = true;
        
        if (campaign.payout > 0) {
          campaignSummary.minPayout = campaignSummary.minPayout !== 0 ? Math.min( campaignSummary.minPayout, campaign.payout ) : campaign.payout;
          campaignSummary.maxPayout = campaignSummary.maxPayout !== 0 ? Math.max( campaignSummary.maxPayout, campaign.payout ) : campaign.payout;
        }
        if (campaignSummary.minPayout !== campaignSummary.maxPayout) {
          campaignSummary.payoutRange = campaignSummary.minPayout.toLocaleString('en-US', {...UDSformat, minimumFractionDigits: 2 }) + " - " + campaignSummary.maxPayout.toLocaleString('en-US', {...UDSformat, minimumFractionDigits: 2 });
        } else if (campaignSummary.minPayout > 0) {
          campaignSummary.payoutRange = campaignSummary.minPayout.toLocaleString('en-US', {...UDSformat, minimumFractionDigits: 2 });
        }
      });
    }
    return campaignSummary;
  }
  const campaignSummary = useMemo(() => calculateCampaignSummary(campaignsResponseDto), [campaignsResponseDto]);

  const modalHeaderTitle = <>{app.title} - ProductID: {app.appID}</>
  const faCircleExclamationIcon = <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "red" }} fixedWidth></FontAwesomeIcon>;

  const handleClose = () => {
    setShowCreateClinkFormData(true);
    closeCreateClink();
  }

  const handleTrafficSourcesChange = (selectedValues: PublisherOrSiteOption[]) => {
    const selectedValue = selectedValues[0];
    const partnerRowData = partnerRowsData.find((partnerRowData) => partnerRowData.trafficSource.id === selectedValue.id);

    if (partnerRowData) {
      handleRemoveRow(partnerRowsData.indexOf(partnerRowData));
    } else {
      handleAddRow(selectedValue);
    }
    const prevState = {...trafficSourcesRef.current.state};
    trafficSourcesRef.current.setState({...prevState, selected: [], text: search});
  }
  const handleTrafficSourcesInputChange = (search:string) => {
    setSearch(search);
  }

  const handleAddRow = (trafficSource:PublisherOrSiteOption) => {
    const publisher:PublisherRecord = (trafficSource.record as PublisherRecord);

    const p2p = applyToAllPartnerRowData.p2p ? applyToAllPartnerRowData.p2p : 0;
    let owner = users.find((user) => user.loginEmail === currentUser.email);
    if (trafficSource.record.apptapPublisherId ===roisenseId) {
      owner = users.find((user) => user.name === "ROISense");
    } else if (trafficSource.record.apptapPublisherId ===trafficMediaId) {
      owner = users.find((user) => user.name === "Traffic Media");
    } else if (applyToAllPartnerRowData.owner) {
      owner = applyToAllPartnerRowData.owner;
    }

    setPartnerRowsData(partnerRowsData => [...partnerRowsData, {
      trafficSource,
      countries: applyToAllPartnerRowData.countries.length ? applyToAllPartnerRowData.countries : countryOptions,
      owner: owner,
      payoutMode: applyToAllPartnerRowData.payoutMode,
      p2p: p2p,
      publisherRevShare: publisher.publisherRevShare,
      tags: [],
      dailyCap: applyToAllPartnerRowData.dailyCap? applyToAllPartnerRowData.dailyCap : 0,
      margin: determineMarginInfo(publisher.publisherRevShare, p2p, campaignSummary)
    }]);
    setValidations([...validations, { "owner": false, "countries": false, "payout": false, "dailyCap": false }]);
  }
  const handleRemoveRow = (index:number) => {
    setPartnerRowsData(partnerRowsData.filter((_, i) => i !== index));
    setValidations(validations.filter((_, i) => i !== index));
  }

  const handlePayoutModeChange = (newPayoutMode:string, index:number) => {
    if (newPayoutMode !== PayoutMode.FIXED) {
      handleClinkPropChange("p2p", index, 0);
    }
    handleClinkPropChange("payoutMode", index, newPayoutMode);
  }

  const handleApplyToAllClinkPropChange = (prop:string, value:any) => {
    setApplyToAllPartnerRowData(previousApplyToAllPartnerRowData => ({
      ...previousApplyToAllPartnerRowData,
      [prop]:value
    }));

    for (let i = 0; i < partnerRowsData.length; i++) {
      if (prop !== "p2p" || partnerRowsData[i].payoutMode === PayoutMode.FIXED) {
        handleClinkPropChange(prop, i, value);
      }
    }
    if (prop === "payoutMode") {
      handleApplyToAllClinkPropChange("p2p", applyToAllPartnerRowData.p2p);
    }
  }

  const handleApplyToAllCountriesKeyDown = (e:any) => {
    if (e.key === 'Enter') {
      const selectedIds = (e.target as HTMLInputElement).value.split(",");
      const countries:CountryOption[] = [...applyToAllPartnerRowData.countries];
      selectedIds.forEach((id:string) => {
        const country = countryOptions.find((country) => country.id.toLowerCase() === id.toLowerCase());
        if (country && !countries.includes(country)) {
          countries.push(country);
        }
      })
      handleApplyToAllClinkPropChange("countries", countries);
      const prevState = {...applyToAllCountriesRef.current.state};
      applyToAllCountriesRef.current.setState({...prevState, text: ""});
    }
  }

  // support_tags/notes
  // const handleApplyToAllTagsKeyDown = (e:any) => {
  //   if (e.code === "Tab" || e.code === "Enter" || e.code === "Space" || e.code === "Comma" || e.code === "NumpadEnter") {
  //     e.preventDefault();
  //     applyToAllTagsRef.current.setState({...applyToAllTagsRef.current.state, text: ""});
  //     const newTag = (e.target as HTMLInputElement).value;
  //     if (newTag.length) {
  //       const currentTags = applyToAllPartnerRowData.tags;
  //       if (currentTags.includes(newTag)) {
  //         currentTags.splice(currentTags.indexOf(newTag), 1);
  //       }
  //       handleApplyToAllClinkPropChange("tags", [...currentTags, newTag]);
  //     }
  //   }
  // }

  const handleDailyCapKeyDown = (e:any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      createButtonRef.current.click();
    }
  };

  const handleClinkPropChange = (prop:string, index:number, value:any) => {
    setPartnerRowsData(partnerRowsData => partnerRowsData.map((partnerRowData, rowIndex:number) => {
      if (rowIndex === index) {
        return {...partnerRowData, [prop]: value};
      } else {
        return partnerRowData;
      }
    }));
    if (prop === "p2p") {
      const newMargin = determineMarginInfo(partnerRowsData[index].publisherRevShare, value, campaignSummary);
      handleClinkPropChange("margin", index, newMargin);
    }
  }

  const handleCountriesKeyDown = (e:any, index:number) => {
    if (e.key === 'Enter') {
      const selectedIds = e.target.value.split(",");
      const countries:CountryOption[] = [...partnerRowsData[index].countries];
      selectedIds.forEach((id:string) => {
        const country = countryOptions.find((country) => country.id.toLowerCase() === id.toLowerCase());
        if (country && !countries.includes(country)) {
          countries.push(country);
        }
      })
      handleClinkPropChange("countries", index, countries);
      const prevState = {...countriesRefs.current[index].state};
      countriesRefs.current[index].setState({...prevState, text: ""});
    }
  }

  // support_tags/notes
  // const handleTagsKeyDown = (e:any, index:number) => {
  //   if (e.code === "Tab" || e.code === "Enter" || e.code === "Space" || e.code === "Comma" || e.code === "NumpadEnter") {
  //     e.preventDefault();
  //     tagsRefs.current[index].setState({...tagsRefs.current[index].state, text: ""});
  //     const newTag = (e.target as HTMLInputElement).value;
  //     if (newTag.length) {
  //       const currentTags = partnerRowsData[index].tags;
  //       if (currentTags.includes(newTag)) {
  //         currentTags.splice(currentTags.indexOf(newTag), 1);
  //       }
  //       handleClinkPropChange("tags", index, [...currentTags, newTag]);
  //     }
  //   }
  // }

  const checkValidations = useCallback(() => {
    const campaignsAdded = campaignsResponseDto && campaignsResponseDto.campaigns ? campaignsResponseDto.campaigns.filter((campaign:any) => campaign.added) : [];
    const hasUnselectedCampaigns = campaignsAdded.length === 0;
    if (hasUnselectedCampaigns) {
      setValidationMessage("No campaigns selected.");
      setDisabled(true);
      return;
    }

    if (partnerRowsData.length === 0) {
      setValidationMessage("No Traffic source selected.");
      setDisabled(true);
      return;
    }

    let isInvalid = false;
    validations.forEach((validation) => {
      Object.values(validation).forEach((prop) => {
        if (prop === false) isInvalid = true;
      })
    });
    if (isInvalid) {
      setValidationMessage("Complete Traffic Source section");
      setDisabled(true);
      return;
    }

    const incentCampaigns = campaignsAdded.reduce((count:number, campaign:any) => campaign.incent ? count + 1 : count, 0);
    if (incentCampaigns > 0 && incentCampaigns !== campaignsAdded.length) {
      setValidationMessage("Cannot mix incent and non-incent campaigns.");
      setDisabled(true);
      return;
    }

    setValidationMessage("");
    setDisabled(false);
  }, [ validations, campaignsResponseDto, partnerRowsData ]);

  const handleCreate = async () => {
    const newMessages:Message[] = [];
    const campaignsAdded = campaignsResponseDto.campaigns.filter((campaign) => campaign.added);

    const clinkData:string[] = [];
    partnerRowsData.forEach((partnerRowData) => {
      const trafficSource = (partnerRowData.trafficSource.record as PublisherSiteRecord);
      const useRevShare = partnerRowData.payoutMode === PayoutMode.REV_SHARE;
      const payout = partnerRowData.p2p ? partnerRowData.p2p : 0;
      const platforms = "AUTO";
      const tags = partnerRowData.tags ? partnerRowData.tags.join(" ") : "";
      const countries = partnerRowData.countries.map((country:CountryOption) => country.id).join(" ");
      const campaignData = trafficSource.apptapPublisherId + "," + trafficSource.apptapSiteId + "," + countries + "," + platforms + "," + payout + "," + partnerRowData.dailyCap + "," + partnerRowData.owner.loginEmail + "," + useRevShare + "," + tags;
      clinkData.push(campaignData);
    });

    const campids = campaignsAdded.map((campaign) => campaign.supplySource + ':' + campaign.campaignID).join(',');
    const params = {
      "appid": app.appID,
      "campids": campids,
      "minPayout": campaignSummary.minPayout,
      "allowIncent": campaignSummary.incent,
      "ignorePlatforms": ignorePlatforms,
      "allowCounterBidOverride": allowCounterBidOverride === "allow" ? true : (allowCounterBidOverride === "disable" ? false : null),
      "allowCounterBid": allowCounterBid === "allow" ? true : (allowCounterBid === "disable" ? false : null),
      "clink" : clinkData,
      "method": "addClinks"
    }
    setShowModalNotification(true);

    const results:any = await Clinks.addClinks(params, setAddClinksIsLoading);

    if (!results.error) {
      results.data.forEach((result:any) => {
        newMessages.push({ message: `cLink ${result.clinkid} Successfully Created`, type: MessageType.INFO });
      });
      setCreationSuccess(true);
      setShowCreateClinkFormData(false);
    } else {
      newMessages.push({ message: `Could not create cLinks: ${results.error}`, type: MessageType.DANGER });
    }
    setMessages(newMessages);
  }
  
  useEffect(() => {
    checkValidations();
  }, [checkValidations]);

  useEffect(() => {
    const newCountryOptions:CountryOption[] = [];
    const campaignsAdded = campaignsResponseDto && campaignsResponseDto.campaigns ? campaignsResponseDto.campaigns.filter((campaign) => campaign.added) : [];
    campaignsAdded.forEach((campaign) => {
      const countryIds = campaign.countries.split(",");
      countryIds.forEach((id:string) => {
        const country = COUNTRIES.find((c) => c.id === id);
        if (country && !newCountryOptions.includes(country)) {
          newCountryOptions.push(country);
        }
      })
    })
    setCountryOptions(newCountryOptions);
    setPartnerRowsData(partnerRowsData => partnerRowsData.map((partnerRowData) => {
      return {
        ...partnerRowData,
        countries: newCountryOptions,
        margin: determineMarginInfo(partnerRowData.publisherRevShare, partnerRowData.p2p, campaignSummary)
      };
    }));
  }, [campaignsResponseDto, campaignSummary]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        Campaigns.getCampaignsByAppId(app.appID, setCampaignsResponseDto);
        TrafficSources.getTrafficSourcesClinkables(setClinkableTrafficSources, true);
        Users.getAssignableUsers(setUsers);  
        // support_tags/notes
        // Tags.getMostFrequentTags(setMostFrequentTags);  
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  }, [app.appID]);

  return (
    <>
      <Modal
        dialogClassName="modal-clink-create"
        show={showCreateClinkFormData} onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        style={{ maxHeight: "95vh" }}
      >
        <ModalHeader title={modalHeaderTitle} useIcon={true} icon={app.icon ? app.icon : null} />
        <Modal.Body style={{ maxHeight: "calc(95vh - 200px)", overflowY: "auto" }}>
          { 
            canEditClinkField('ALL_FIELDS') 
            ? <CampaignsTable campaignsResponseDto={campaignsResponseDto} setCampaignsResponseDto={setCampaignsResponseDto} />
            : null
          }

          <Card>
            <Card.Header style={cardHeaderStyle}>Selected Campaign Summary</Card.Header>
            <Card.Body>
              <table style={{ minWidth: "50%" }}>
                <tbody>
                  <tr>
                    <th style={{ width: "180px" }}>Number of Campaigns:</th><td>{campaignSummary.numberOfCampaigns}</td>
                    <th style={{ width: "180px" }}>Payout Range:</th><td>{campaignSummary.payoutRange}</td>
                  </tr>
                  <tr>
                    <th style={{ width: "180px" }}>Platforms:</th><td style={{ width: "150px" }}>{campaignSummary.platforms.length ? campaignSummary.platforms.join(",") : "None"}</td>
                    <th style={{ width: "180px", backgroundColor: "#FFF380"}}>Total Cap:</th>
                    <td>
                      {
                        (campaignSummary.totalCap > 0) ?
                          <span style={{ backgroundColor: "#FFF380", padding: "1px 8px 2px 0"}}>{campaignSummary.totalCap}</span>
                        :
                          <span style={{ padding: "1px 8px 2px 0"}}>None</span> 
                      }
                    </td>
                  </tr>
                  <tr>
                    <th style={{ width: "180px" }}>Countries:</th><td>{campaignSummary.countries.length ? campaignSummary.countries.join(",") : "None"}</td>
                    <th style={{ width: "180px" }}>OS Version:</th><td>{campaignSummary.osVersion}</td>
                  </tr>
                  <tr>
                    <th style={{ width: "180px" }}>Allow Incent:</th><td>{campaignSummary.numberOfCampaigns === 0 ? "None" : ( campaignSummary.incent ? "Yes" : "No" )}</td>
                    <th /><td />
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>

          <br />
          <ExistingClinksTable existingCLinks={existingCLinks} isLoading={campaignsResponseDto?.status === ResponseStatus.LOADING} />
          <br />

          <Card>

            <Card.Header style={cardHeaderStyle}>Traffic Sources &nbsp;-&nbsp; A separate cLink is created for each Traffic Source selected</Card.Header>
            <Card.Body>
              <Row xl={10} lg={4} md={2} style={{ marginBottom: "1rem" }}>
                <Col xl={4}>
                  {
                    clinkableTrafficSources ? 
                      <TrafficSourcesAutocomplete
                        onChange={(e : PublisherOrSiteOption[]) => handleTrafficSourcesChange(e)} 
                        options={clinkableTrafficSources}
                        reactRef={trafficSourcesRef}
                        onInputChange={handleTrafficSourcesInputChange}
                        multiple
                        useCheckbox
                        usePublisherDisabled
                        selectedForCheckboxes={partnerRowsData.map((partnerRowData => partnerRowData.trafficSource))}
                      />
                    : null
                  }
                </Col>
              </Row>
              <Table>
                <thead>
                  <tr>
                    <th style={{ minWidth: "300px" }}>Partner</th>
                    <th style={{ width: "200px" }}>Owner</th>
                    <th style={{ width: "280px" }}>Countries</th>
                    <th style={{ width: "180px" }}>Payout</th>
                    <th style={{ width: "160px" }}></th>
                    <th style={{ width: "250px" }}>Margin</th>
                    {/* support_tags/notes 
                    <th style={{ width: "250px" }}>Tags</th> */}
                    <th style={{ width: "120px" }}>Cap</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr className="card-header">
                    <td>
                      <span style={{ display: "flex", marginTop: "0.2rem" }}>Apply to all</span>
                    </td>
                    <td>
                      <AutocompleteSingle
                        size="sm"
                        id="fowner"
                        placeholder="No Owner"
                        labelKey={(user:User) => user.name}
                        options={users}
                        style={{ fontSize: 12 }}
                        onChange={(e:User[]) => handleApplyToAllClinkPropChange("owner", e[0])}
                        selected={users.filter((user) => applyToAllPartnerRowData.owner && applyToAllPartnerRowData.owner.loginEmail && applyToAllPartnerRowData.owner.loginEmail === user.loginEmail)}
                      />
                    </td>
                    <td>
                      <CountriesAutocomplete
                        onKeyDown={(e:React.SyntheticEvent) => handleApplyToAllCountriesKeyDown(e)}
                        onChange={(e:React.SyntheticEvent) => handleApplyToAllClinkPropChange("countries", e)}
                        options={countryOptions}
                        selected={applyToAllPartnerRowData.countries}
                        reactRef={applyToAllCountriesRef}
                      />
                    </td>
                    <td>
                      <span style={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline" }}>
                        <ToggleButtonGroup type="radio" name="applyToAllPayoutMode" value={applyToAllPartnerRowData.payoutMode} onChange={(newPayoutMode:string) => handleApplyToAllClinkPropChange("payoutMode", newPayoutMode)}>
                          <ToggleButton value="noPyout" size="sm" className={applyToAllPartnerRowData.payoutMode === "noPyout" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px"}}>No payout</ToggleButton>
                          <ToggleButton value="fixed" size="sm" className={applyToAllPartnerRowData.payoutMode === "fixed" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Fixed</ToggleButton>
                        </ToggleButtonGroup>
                      </span>
                    </td>
                    <td>
                      <span style={{ display: applyToAllPartnerRowData.payoutMode === PayoutMode.FIXED ? "flex" : "none", alignItems: "baseline" }}>
                        <span style={{ marginRight: "0.2rem" }}>$</span>
                        <Form.Control
                          as="input"
                          type="number"
                          size="sm"
                          step="0.01"
                          onFocus={() => applyToAllP2pRef.current?.select()}
                          onChange={(e) => handleApplyToAllClinkPropChange("p2p", e.target.value)}
                          value={applyToAllPartnerRowData.p2p}
                          ref={applyToAllP2pRef}
                          style={{ width: "70px", fontSize: 12 }}
                        />
                      </span>
                    </td>
                    <td />
                    {/* support_tags/notes 
                    <td>
                      <TagsAutocomplete
                        onChange={(e:any) => handleApplyToAllClinkPropChange("tags", e.map((tag:any) => tag.label ? tag.label : tag))}
                        onKeyDown={(e) => handleApplyToAllTagsKeyDown(e)}
                        options={mostFrequentTags}
                        selected={applyToAllPartnerRowData.tags}
                        reactRef={applyToAllTagsRef}
                      />
                    </td>*/}
                    <td>
                      <Form.Control
                        as="input"
                        type="number"
                        size="sm"
                        ref={applyToAllDailyCapRef}
                        onFocus={() => applyToAllDailyCapRef.current?.select()}
                        onChange={(e) => handleApplyToAllClinkPropChange("dailyCap", e.target.value)}
                        onKeyDown={handleDailyCapKeyDown}
                        value={applyToAllPartnerRowData.dailyCap}
                      />
                    </td>
                    <td />
                  </tr>
                  {
                    partnerRowsData.map((partnerRowData, index:number) => {
                      return (
                        <tr key={index}>
                          <td>
                            <span style={{ display: "flex", marginTop: "0.2rem" }}>
                              {TrafficSourceLabelKeyFunction(partnerRowData.trafficSource)}
                            </span>
                          </td>
                          <td>
                            <ValidationsWrapper
                              name={"owner"}
                              required
                              isAutocomplete
                              setValidations={setValidations}
                              defaultDirty={partnerRowData.owner ? true : false}
                              index={index}
                              value={users.filter((user) => partnerRowData.owner && partnerRowData.owner.loginEmail && partnerRowData.owner.loginEmail === user.loginEmail)}
                            >
                              <AutocompleteSingle
                                size="sm"
                                id="fowner"
                                placeholder="No Owner"
                                labelKey={(user:User) => user.name}
                                options={users}
                                style={{ fontSize: 12 }}
                                onChange={(e:any) => handleClinkPropChange("owner", index, e[0])}
                                selected={users.filter((user) => partnerRowData.owner && partnerRowData.owner.loginEmail && partnerRowData.owner.loginEmail === user.loginEmail)}
                              />
                            </ValidationsWrapper>
                          </td>
                          <td>
                            <ValidationsWrapper
                              name={"countries"}
                              required
                              isAutocomplete
                              index={index}
                              setValidations={setValidations}
                              value={partnerRowData.countries}
                            >
                              <CountriesAutocomplete
                                onKeyDown={(e:React.SyntheticEvent) => handleCountriesKeyDown(e, index)}
                                onChange={(e:React.SyntheticEvent) => handleClinkPropChange("countries", index, e)}
                                options={countryOptions}
                                selected={partnerRowData.countries}
                                reactRef={(ref:any) => countriesRefs.current[index] = ref}
                              />
                            </ValidationsWrapper>
                          </td>
                          <td>
                            <span style={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline" }}>
                              <ToggleButtonGroup type="radio" name="payoutMode" value={partnerRowData.payoutMode} onChange={(newPayoutMode:string) => handlePayoutModeChange(newPayoutMode, index)}>
                                {
                                  partnerRowData.publisherRevShare && partnerRowData.publisherRevShare > 0 ? <ToggleButton value="revShare" size="sm" className={partnerRowData.payoutMode === "revShare" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Rev Share</ToggleButton> : null
                                }
                                <ToggleButton value="noPyout" size="sm" className={partnerRowData.payoutMode === "noPyout" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px"}}>No payout</ToggleButton>
                                <ToggleButton value="fixed" size="sm" className={partnerRowData.payoutMode === "fixed" ? "active-toggle-button" : "inactive-toggle-button"} style={{ whiteSpace: "nowrap", fontSize: "12px" }}>Fixed</ToggleButton>
                              </ToggleButtonGroup>
                            </span>
                          </td>
                          <td>
                            {
                              partnerRowData.payoutMode === PayoutMode.REV_SHARE ?
                                <Form.Control
                                  as="input"
                                  type="string"
                                  size="sm"
                                  disabled
                                  value={determineRevShareInfo(partnerRowData.publisherRevShare, campaignSummary)}
                                />
                              : 
                                null
                            }
                            <span style={{ display: partnerRowData.payoutMode === PayoutMode.FIXED ? "flex" : "none", alignItems: "baseline" }}>
                              <span style={{ marginRight: "0.2rem" }}>$</span>
                              <ValidationsWrapper
                                name={"payout"}
                                index={index}
                                min={partnerRowData.payoutMode === PayoutMode.FIXED ? 0.01 : null}
                                required={partnerRowData.payoutMode === PayoutMode.FIXED }
                                setValidations={setValidations}
                                value={partnerRowData.p2p}
                                style={{ display: "flex", width: "70px" }}
                              >
                                <Form.Control
                                  as="input"
                                  type="number"
                                  size="sm"
                                  step="0.01"
                                  onFocus={() => payoutRefs.current[index]?.select()}
                                  disabled={partnerRowData.payoutMode !== "fixed"}
                                  onChange={(e) => handleClinkPropChange("p2p", index, e.target.value)}
                                  value={partnerRowData.p2p}
                                  ref={(ref:any) => payoutRefs.current[index] = ref}
                                  style={{ marginRight: "20px", fontSize: 12 }}
                                />
                              </ValidationsWrapper>
                            </span>
                          </td>
                          <td>
                            <span style={{ display: "flex", marginTop: "0.3rem", fontSize: 12 }}>{partnerRowData.margin}</span>
                          </td>
                          {/* support_tags/notes 
                          <td>
                            <TagsAutocomplete
                              onChange={(e:any) => handleClinkPropChange("tags", index, e.map((tag:any) => tag.label ? tag.label : tag))}
                              onKeyDown={(e:React.SyntheticEvent) => handleTagsKeyDown(e, index)}
                              options={mostFrequentTags}
                              selected={partnerRowData.tags}
                              reactRef={(ref) => tagsRefs.current[index] = ref}
                            />
                          </td> */}
                          <td>
                            <ValidationsWrapper
                              name={"dailyCap"}
                              index={index}
                              min={1}
                              max={campaignSummary.totalCap > 0 && campaignSummary.totalCap < 1000000 ? campaignSummary.totalCap : 1000000}
                              required
                              setValidations={setValidations}
                              value={partnerRowData.dailyCap}
                            >
                              <Form.Control
                                as="input"
                                type="number"
                                size="sm"
                                ref={(ref:any) => dailyCapRefs.current[index] = ref}
                                onFocus={() => dailyCapRefs.current[index]?.select()}
                                onChange={(e) => handleClinkPropChange("dailyCap", index, e.target.value)}
                                onKeyDown={handleDailyCapKeyDown}
                                value={partnerRowData.dailyCap}
                              />
                            </ValidationsWrapper>
                          </td>
                          <td>
                            <Button size="sm" variant="primary" onClick={()=>handleRemoveRow(index)} style={{ right: 0, fontSize: "12px" }}>
                              <FontAwesomeIcon icon={faMinus} style={{ color: "white" }}/>
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          <br />

          <Card>
            <Card.Header style={cardHeaderStyle}>Targeting Overrides</Card.Header>
            <Card.Body>
              <Form.Group controlId="useIgnorePlatforms" >
                <Form.Check
                  inline
                  style={{ whiteSpace: "nowrap", margin: "5px 0 0 5px" }}
                  type="checkbox"
                  checked={ignorePlatforms}
                  onChange={(e) => setIgnorePlatforms(e.target.checked)}
                  label="Ignore platforms and OS version targeting on clicks"
                />
              </Form.Group>
            </Card.Body>
          </Card>
          <br />

          <Card>
            <Card.Header style={cardHeaderStyle}>Counter Bidding Overrides<InfoIcon style={{ marginLeft: "0.5rem" }} id={"overrideCounterBidingTooltip"}><OverrideCounterBidingTooltip /></InfoIcon></Card.Header>
            <Card.Body>
              <span >
                Admin: <InfoIcon><AdminCounterBidingTooltip /></InfoIcon>
                <ToggleButtonGroup type="radio" name="allowCounterBidOverride" style={{ margin: "0 10px"}} value={allowCounterBidOverride} onChange={(e:string) => setAllowCounterBidOverride(e)}>
                  <ToggleButton value="default" size="sm" className={allowCounterBidOverride === "default" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is based on current publisher configuration or campaign configuration or partner clink counter bidding setting.">Default</ToggleButton>
                  <ToggleButton value="allow" size="sm" className={allowCounterBidOverride === "allow" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is allowed, regardless of current publisher configuration, if campaign is configured to allow.">Allow</ToggleButton>
                  <ToggleButton value="disable" size="sm" className={allowCounterBidOverride === "disable" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is disabled, regardless of any other configuration setting, including the partner clink counter bidding setting.">Disable</ToggleButton>
                </ToggleButtonGroup>
              </span>
              <span>
                Partner:
                <ToggleButtonGroup type="radio" name="allowCounterBid" style={{ margin: "0 10px"}} value={allowCounterBid} onChange={(e:string) => setAllowCounterBid(e)}>
                  <ToggleButton value="default" size="sm" className={allowCounterBid === "default" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is based on current publisher configuration or campaign configuration.">Default</ToggleButton>
                  <ToggleButton value="allow" size="sm" className={allowCounterBid === "allow" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is allowed, regardless of current publisher configuration, if campaign is configured to allow. Can be overridden by the admin clink counter bidding setting.">Allow</ToggleButton>
                  <ToggleButton value="disable" size="sm" className={allowCounterBid === "disable" ? "active-toggle-button" : "inactive-toggle-button"} title="Counter bidding is disabled, regardless of any other configuration setting.">Disable</ToggleButton>
                </ToggleButtonGroup>
              </span>

            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Card style={{ width: "100%", margin: 0 }}>
            <Card.Body style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0.5rem" }}>
              <div>{ disabled ? <Alert style={{ margin: 0 }}>{faCircleExclamationIcon} {validationMessage}</Alert> : null }</div>
              <div style={{ display: "flex", justifyContent: "end" }}>
                <Button variant="secondary" style={{ margin: "5px" }} onClick={handleClose}>Cancel</Button>
                <Button variant="primary" ref={createButtonRef} style={{ margin: "5px" }} disabled={disabled} onClick={handleCreate}>Create</Button>
              </div>
            </Card.Body>
          </Card>
        </Modal.Footer>
      </Modal>
      <ModalNotificaton
        title="Create Clinks"
        messages={messages}
        show={showModalNotification}
        setShow={setShowModalNotification}
        isLoading={addClinksIsLoading}
        handleParentClose={creationSuccess ? closeCreateClink : null}
      />
    </>
  );
}

export default ModalCreateClink;
